import { offlineDatabase } from '../database/offlineDatabase/offlineDatabase';
import { OfflineDatabaseClassification } from '../database/offlineDatabase/types/offlineDatabaseClassification.types';
import { createOfflineCommonRepository } from './common/createOfflineCommonRepository';
import { createOfflineFilterOptionsRepository } from './filterOptions/createOfflineFilterOptionsRepository';
import { PageByIdsAndClassificationTypeIdParams } from './filterOptions/types/offlineClassifications.repository.types';
import { offlineClassificationTypesRepository } from './offlineClassificationTypes.repository';

const commonRepository = createOfflineCommonRepository<OfflineDatabaseClassification, 'id'>(
  () => offlineDatabase.classifications,
);

const filterOptionsRepository = createOfflineFilterOptionsRepository<
  OfflineDatabaseClassification,
  'id'
>(() => offlineDatabase.classifications, ['code', 'name']);

export const offlineClassificationsRepository = {
  ...commonRepository,
  ...filterOptionsRepository,
  pageByIdsAndClassificationTypeId: async ({
    ids,
    skip,
    limit,
    orderBy,
    classificationTypeId,
  }: PageByIdsAndClassificationTypeIdParams) => {
    const databaseTable = offlineDatabase.classifications;
    const idSet = new Set(ids);

    const filtered = databaseTable
      .orderBy(orderBy)
      .filter(
        (item: OfflineDatabaseClassification) =>
          idSet.has(item.id) && item.classificationTypeId === classificationTypeId,
      );

    const [count, data] = await Promise.all([
      filtered.count(),
      filtered.offset(skip).limit(limit).toArray(),
    ]);

    return {
      count,
      data,
    };
  },
  findByIdsAndClassificationTypeId: async (
    ids: string[],
    classificationTypeId: string,
  ): Promise<OfflineDatabaseClassification[]> => {
    const databaseTable = offlineDatabase.classifications;

    return databaseTable
      .where('id')
      .anyOf(ids)
      .and((item) => item.classificationTypeId === classificationTypeId)
      .toArray();
  },
  getClassificationsToUseAsTagByIds: async (classificationIds: string[]) => {
    const classifications = await offlineClassificationsRepository.findByIds(classificationIds);
    const classificationTypes = await offlineClassificationTypesRepository.findAllUsedAsTag();
    const classificationTypeSet = new Set(classificationTypes.map((ct) => ct.id));
    const classificationsToUseAsTag = classifications.filter(
      (classification) => !!classificationTypeSet.has(classification.classificationTypeId),
    );

    return classificationsToUseAsTag;
  },
};
